import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCanCheck } from '@/ability/helpers';
import StatusBadge from '@/components/ui/StatusBadge';
import { getBillingRequests, respondBillingRequest } from '@services/billing.service';

export const BILLING_REQUESTS_QUERY_KEY = ['billing', 'requests'];

const STATUS_TAB = { ALL: 'all', PENDING: 'pending', ACCEPTED: 'accepted', DECLINED: 'declined' };
const capFirst = (s) => (s && s.length ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '—');

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
        return new Date(dateStr).toLocaleString();
    } catch {
        return dateStr;
    }
};

/**
 * Billing requests list logic: data, filters, table config, panel state, permissions, respond mutation.
 * No GET by id – detail is the selected row from list.
 */
export const useBillingRequests = () => {
    const queryClient = useQueryClient();
    const [searchText, setSearchText] = useState('');
    const [statusTab, setStatusTab] = useState(STATUS_TAB.ALL);
    const [panelOpen, setPanelOpen] = useState(false);
    const [selectedRequestId, setSelectedRequestId] = useState(null);

    const can = useCanCheck();
    const canView = can('billing.requests.view');
    const canRequest = can('billing.requests.request');
    const canRespond = can('billing.requests.respond');

    const { data: response, isError: requestsError } = useQuery({
        queryKey: [...BILLING_REQUESTS_QUERY_KEY, { page: 1, limit: 100 }],
        queryFn: () => getBillingRequests({ page: 1, limit: 100 }),
        retry: false,
    });

    const requests = response?.data ?? [];
    const total = response?.total ?? 0;

    const pendingCount = useMemo(
        () => requests.filter((r) => (r.status || '').toLowerCase() === 'pending').length,
        [requests],
    );
    const acceptedCount = useMemo(
        () => requests.filter((r) => (r.status || '').toLowerCase() === 'accepted').length,
        [requests],
    );
    const declinedCount = useMemo(
        () => requests.filter((r) => (r.status || '').toLowerCase() === 'declined').length,
        [requests],
    );

    const selectedRequest = useMemo(
        () => requests.find((r) => r.id === selectedRequestId) ?? null,
        [requests, selectedRequestId],
    );
    const selectedRequestTitle = useMemo(() => {
        if (!selectedRequest) return 'Request details';
        const name = selectedRequest.project?.name ?? selectedRequest.audit?.name ?? null;
        return name ? `Billing: ${name}` : `Request ${selectedRequest.id?.slice(0, 8) ?? ''}`;
    }, [selectedRequest]);

    const filteredRequests = useMemo(() => {
        let list = requests;
        const statusLower = (s) => (s || '').toLowerCase();
        if (statusTab === STATUS_TAB.PENDING) list = list.filter((r) => statusLower(r.status) === 'pending');
        if (statusTab === STATUS_TAB.ACCEPTED) list = list.filter((r) => statusLower(r.status) === 'accepted');
        if (statusTab === STATUS_TAB.DECLINED) list = list.filter((r) => statusLower(r.status) === 'declined');
        if (searchText.trim()) {
            const q = searchText.toLowerCase();
            list = list.filter(
                (r) =>
                    (r.project?.name || '').toLowerCase().includes(q) ||
                    (r.audit?.name || '').toLowerCase().includes(q) ||
                    (r.requestedByUser?.name || '').toLowerCase().includes(q) ||
                    (r.requestedByUser?.email || '').toLowerCase().includes(q),
            );
        }
        return list;
    }, [requests, statusTab, searchText]);

    const tableData = useMemo(
        () =>
            filteredRequests.map((r) => ({
                key: r.id,
                id: r.id,
                projectOrAudit: r.project?.name ?? r.audit?.name ?? '—',
                requestedBy: r.requestedByUser?.name ?? r.requestedByUser?.email ?? '—',
                requestedAt: r.requestedAt,
                status: r.status,
                clientRespondedAt: r.clientRespondedAt,
                clientResponse: r.clientResponse,
                clientNote: r.clientNote,
                clientRating: r.clientRating,
            })),
        [filteredRequests],
    );

    const respondMutation = useMutation({
        mutationFn: ({ requestId, body }) => respondBillingRequest(requestId, body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: BILLING_REQUESTS_QUERY_KEY });
        },
    });

    const columns = useMemo(
        () => [
            {
                title: 'Project / Audit',
                dataIndex: 'projectOrAudit',
                key: 'projectOrAudit',
            },
            {
                title: 'Requested by',
                dataIndex: 'requestedBy',
                key: 'requestedBy',
            },
            {
                title: 'Requested at',
                dataIndex: 'requestedAt',
                key: 'requestedAt',
                width: 160,
                render: (v) => formatDate(v),
            },
            {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                width: 100,
                render: (_, record) => <StatusBadge status={record.status} />,
            },
        ],
        [],
    );

    const openPanel = (id) => {
        setSelectedRequestId(id);
        setPanelOpen(true);
    };
    const closePanel = () => {
        setPanelOpen(false);
        setSelectedRequestId(null);
    };

    return {
        requests,
        total,
        pendingCount,
        acceptedCount,
        declinedCount,
        filteredRequests,
        tableData,
        columns,
        searchText,
        setSearchText,
        statusTab,
        setStatusTab,
        STATUS_TAB,
        panelOpen,
        setPanelOpen,
        selectedRequestId,
        selectedRequest,
        selectedRequestTitle,
        openPanel,
        closePanel,
        canView,
        canRequest,
        canRespond,
        respondMutation,
        requestsError,
        capFirst,
        formatDate,
    };
};
